import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { DeadlineNote, AutoApprovedNote } from '../components/ApprovalBits.jsx';
import { useAuth } from '../AuthContext.jsx';
import { clientPath, useSwitchToOwningClient } from '../lib/clientLink.js';
import WhyThisPost from '../components/WhyThisPost.jsx';
import Working, { pollUntil } from '../components/Working.jsx';
import MicButton from '../components/MicButton.jsx';
import { useDraft } from '../lib/draft.js';
// Session 32: the review experience itself lives here now, so the ad hoc page
// can render the same thing instead of a copy that drifts.
import {
  ReviewCallout, PostAndEditor, ImageSlotsSection, useSlotUploads,
} from '../components/DraftReview.jsx';
import PostingInstructions from '../components/PostingInstructions.jsx';

const SIGNED_URL_TTL = 900;

function formatDate(iso) {
  if (!iso) return '';
  // Date-only strings get a noon anchor; full timestamps parse as they are.
  const d = typeof iso === 'string' && iso.length === 10 ? new Date(iso + 'T12:00:00Z') : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

function tomorrowIsoPT() {
  const now = new Date();
  // Best-effort local-tomorrow; browser TZ will be close enough for the min-date
  // constraint. Server RPC enforces the real America/Los_Angeles bound.
  const t = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return t.toISOString().slice(0, 10);
}

export default function BlogReview() {
  const { slug } = useParams();
  // activeClient carries the platform, which picks the posting instructions.
  const { activeId, activeClient } = useAuth();
  const navigate = useNavigate();

  const { isAgency } = useAuth();
  // Session 37: agency only, and purely presentational. The server checks the
  // caller's role on every write, so this cannot grant anything — it stops the
  // page hiding the client's own controls, which is the only way to see what a
  // client sees on a draft that is currently their turn.
  const [viewAsClient, setViewAsClient] = useState(false);
  const asAgency = isAgency && !viewAsClient;
  // Session 21: on Digital Lotus's own posts the agency is the client, so it
  // gets the client's Approve / Request changes while the draft waits on DL.
  const dlOwner = asAgency && activeId === 'digitallotus';
  const [state,       setState]       = useState({ loading: true, error: null, draft: null, previewHtml: null });
  const [changesNote, setChangesNote] = useState('');
  const [showModal,   setShowModal]   = useState(null); // 'approve' | 'changes' | null
  const [publishDate, setPublishDate] = useState('');
  const [busy,        setBusy]        = useState(false);
  const [banner,      setBanner]      = useState(null);
  const [previewPending, setPreviewPending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);      // fix A: manual refresh in flight
  const [working, setWorking] = useState(null);             // fix A: {text, done} banner for background jobs
  const [revising, setRevising] = useState(false);
  const pendingRef = useRef({ deadline: 0, seenUploadedAt: null });
  const previewRef = useRef(null);
  const changesRef = useRef(null);      // Session 9: MicButton caret target
  // Request changes is on offer (mirrors canAct and the agency-review buttons
  // below); only then is an unsent note restored, reopening its box.
  const ds = state.draft?.status;
  const canRequestChanges = !!ds && (ds === 'agency_review' ? asAgency
    : ds !== 'approved' && ds !== 'flagged' && !(asAgency && !dlOwner && ['sent', 'awaiting_reply'].includes(ds)));
  const clearChangesDraft = useDraft(canRequestChanges ? `review.changes.${state.draft.job_id}` : null,
    changesNote, setChangesNote, () => setShowModal('changes'));
  const isAgencyReviewNow = state.draft?.status === 'agency_review';

  async function loadDraft() {
    setState((s) => ({ ...s, loading: true, error: null, notFound: false }));
    if (!activeId || !slug) return;
    const { data: rows, error: qErr } = await supabase
      .from('v_portal_draft').select('*')
      .eq('client_id', activeId).eq('slug', slug).limit(1);
    if (qErr) { setState({ loading: false, error: qErr.message, draft: null, previewHtml: null }); return; }
    const draft = rows && rows[0];
    if (!draft) { setState({ loading: false, error: 'Draft not found — it may have moved on already.', draft: null, previewHtml: null, notFound: true }); return; }

    let previewHtml = null;
    if (draft.preview_package_path) {
      const { data: sig, error: sErr } = await supabase.storage
        .from('post-packages').createSignedUrl(draft.preview_package_path, SIGNED_URL_TTL);
      if (!sErr) {
        try {
          const resp = await fetch(sig.signedUrl);
          if (resp.ok) previewHtml = await resp.text();
        } catch (e) { console.warn('preview fetch failed:', e); }
      }
    }
    setState({ loading: false, error: null, draft, previewHtml });
  }

  async function refreshPreviewOnly() {
    if (!activeId || !slug) return;
    const { data: rows } = await supabase
      .from('v_portal_draft').select('*')
      .eq('client_id', activeId).eq('slug', slug).limit(1);
    const draft = rows && rows[0];
    if (!draft) return;
    let previewHtml = null;
    if (draft.preview_package_path) {
      const { data: sig, error: sErr } = await supabase.storage
        .from('post-packages').createSignedUrl(draft.preview_package_path, SIGNED_URL_TTL);
      if (!sErr) {
        try {
          const resp = await fetch(sig.signedUrl);
          if (resp.ok) previewHtml = await resp.text();
        } catch (e) { console.warn('preview refetch failed:', e); }
      }
    }
    setState((s) => ({ ...s, draft, previewHtml }));
    return draft;
  }

  useEffect(() => { loadDraft(); }, [activeId, slug]); // eslint-disable-line react-hooks/exhaustive-deps
  // Session 11: opened without ?client= under the wrong client -> switch to the draft's client.
  useSwitchToOwningClient('v_portal_draft', slug, !!state.notFound);

  // Fix A: a manual refresh shows it is working, waits for a newer preview if
  // one is being rebuilt, then reloads and says so.
  async function onRefreshPreview() {
    if (refreshing) return;
    setRefreshing(true);
    setWorking({ text: 'Updating your preview. This can take a few minutes.', done: false });
    const seen = state.draft?.preview_uploaded_at || null;
    const fillsAt = state.draft?.image_slot_fills_updated_at || null;
    // Session 66: a saved edit counts as a pending rebuild too.
    const editAt = state.draft?.body_md_edited ? (state.draft?.body_edited_at || null) : null;
    const newer = (ts) => !!ts && (!seen || new Date(ts) > new Date(seen));
    const rebuildPending = newer(fillsAt) || newer(editAt);
    if (rebuildPending) {
      await pollUntil(async () => {
        const { data: rows } = await supabase.from('v_portal_draft').select('preview_uploaded_at')
          .eq('client_id', activeId).eq('slug', slug).limit(1);
        const cur = rows?.[0]?.preview_uploaded_at || null;
        return cur && cur !== seen;
      }, { every: 10000, timeout: 240000 });
    }
    await refreshPreviewOnly();
    setRefreshing(false);
    setPreviewPending(false);
    setWorking({ text: 'Preview updated.', done: true });
    setTimeout(() => setWorking((w) => (w && w.done ? null : w)), 6000);
  }

  // Fix A: after Request changes, watch the draft until the revision lands.
  useEffect(() => {
    if (!revising) return undefined;
    let cancelled = false;
    (async () => {
      let landed = null;
      const ok = await pollUntil(async () => {
        const { data: rows } = await supabase.from('v_portal_draft').select('status, gate_pending')
          .eq('client_id', activeId).eq('slug', slug).limit(1);
        if (rows?.[0] && rows[0].status !== 'flagged') { landed = rows[0]; return true; }
        return false;
      }, { every: 15000, timeout: 20 * 60000 });
      if (cancelled) return;
      setRevising(false);
      await loadDraft();
      // Hotfix: a revision behind the agency gate is not ready for the
      // client yet; the reloaded page shows the "We're updating" banner.
      if (ok && landed?.gate_pending && !asAgency) setWorking(null);
      else setWorking(ok ? { text: 'Revised draft is ready.', done: true }
                         : { text: 'The revision is taking longer than usual. We will email you when it is ready.', done: false, tone: 'info' });
    })();
    return () => { cancelled = true; };
  }, [revising]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll v_portal_draft.preview_uploaded_at when a fill just landed. Stops
  // when the preview timestamp advances past the pre-upload snapshot or
  // when the 3-minute deadline is reached.
  useEffect(() => {
    if (!previewPending) return undefined;
    const tick = async () => {
      if (Date.now() > pendingRef.current.deadline) {
        setPreviewPending(false);
        return;
      }
      const draft = await refreshPreviewOnly();
      const current = draft?.preview_uploaded_at || null;
      const seen = pendingRef.current.seenUploadedAt;
      if (current && current !== seen) {
        setPreviewPending(false);
        setWorking({ text: 'Preview updated.', done: true });
        setTimeout(() => setWorking((w) => (w && w.done ? null : w)), 6000);
      }
    };
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, [previewPending]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.draft?.publish_date) setPublishDate(state.draft.publish_date);
  }, [state.draft?.publish_date]);

  const fills = useMemo(() => {
    const arr = state.draft?.image_slot_fills || [];
    const by = {};
    for (const f of arr) if (f && f.slot_id) by[f.slot_id] = f;
    return by;
  }, [state.draft]);

  // Session 66: a saved edit (or a reset) is laid out on the droplet within
  // a minute; watch for the newer preview the same way a new photo is watched.
  async function onTextSaved() {
    setWorking({ text: 'Laying out your changes. The styled preview updates in a minute or two.', done: false });
    startPreviewPending();
    await loadDraft();
  }

  function startPreviewPending() {
    pendingRef.current = {
      deadline: Date.now() + 180000,
      seenUploadedAt: state.draft?.preview_uploaded_at || null,
    };
    setPreviewPending(true);
  }

  // Session 32: shared with the ad hoc review page. onFill is this page's own
  // business: a new photo means the preview package is being rebuilt, and the
  // poller below watches preview_uploaded_at until it advances.
  const { uploading, skipping, onUploadSlot, onToggleSkip } = useSlotUploads({
    jobId: state.draft?.job_id,
    clientId: activeId,
    slug,
    reload: loadDraft,
    onBanner: setBanner,
    onFill: (why) => {
      // Session 60: "No photo here" takes the spot out of the preview; say so
      // instead of promising a new photo that is not coming.
      const text = why === 'skip'   ? 'Updating your preview without that photo. This takes about a minute.'
                 : why === 'unskip' ? 'Putting that photo spot back in your preview. This takes about a minute.'
                 : 'Updating your preview with the new photo. This can take a few minutes.';
      setWorking({ text, done: false });
      startPreviewPending();
    },
  });

  const approveBlockers = useMemo(() => {
    const d = state.draft;
    if (!d) return [];
    const slots = d.image_slots || [];
    const missing = [];
    for (const s of slots) {
      const f = fills[s.slot_id];
      const filled  = !!(f?.storage_path || f?.image_path);
      const skipped = !!f?.skipped;
      if (s.slot_id === 'hero') {
        if (!filled) missing.push('Hero image');
      } else if (!filled && !skipped) {
        missing.push(`${s.slot_id} (upload or skip)`);
      }
    }
    return missing;
  }, [state.draft, fills]);

  async function onApprove() {
    setBusy(true);
    try {
      const params = { p_job_id: state.draft.job_id };
      if (publishDate && publishDate !== state.draft.publish_date) {
        params.p_publish_date = publishDate;
      }
      const { error } = await supabase.rpc('portal_approve', params);
      if (error) throw error;
      setBanner({ tone: 'ok', text: 'Approved. Scheduling published post…' });
      setShowModal(null);
      setTimeout(() => navigate('/'), 900);
    } catch (e) {
      setBanner({ tone: 'err', text: `Couldn't approve: ${e.message || e}` });
      setShowModal(null);
    } finally { setBusy(false); }
  }

  // Session 6 follow-up (fix C): approved, not yet published or delivered -> back to draft.
  async function onReopen() {
    setBusy(true);
    try {
      const { error } = await supabase.rpc('portal_reopen_draft', { p_job_id: state.draft.job_id });
      if (error) throw error;
      setShowModal(null);
      setBanner({ tone: 'ok', text: 'Moved back to draft. The scheduled publish is cancelled.' });
      await loadDraft();
    } catch (e) {
      setBanner({ tone: 'err', text: `Couldn't move it back: ${e.message || e}` });
      setShowModal(null);
    } finally { setBusy(false); }
  }

  // Session 6 Part C: release a gated draft to the client.
  async function onRelease() {
    setBusy(true);
    try {
      const { error } = await supabase.rpc('portal_release_draft', { p_job_id: state.draft.job_id });
      if (error) throw error;
      setShowModal(null);
      setBanner({ tone: 'ok', text: state.draft?.approval_mode === 'agency_only'
        ? 'Approved (final). The publish job is set up for the scheduled date on the next reactor pass.'
        : 'Sent to the client. They get the "draft ready" email on the next reactor pass.' });
      setTimeout(() => navigate('/'), 1200);
    } catch (e) {
      setBanner({ tone: 'err', text: `Couldn't send: ${e.message || e}` });
      setShowModal(null);
    } finally { setBusy(false); }
  }

  async function onRequestChanges() {
    if (!changesNote.trim()) { setBanner({ tone: 'err', text: 'Add a short note first.' }); return; }
    setBusy(true);
    try {
      const { error } = await supabase.rpc('portal_request_changes', {
        p_job_id: state.draft.job_id, p_notes: changesNote.trim(),
      });
      if (error) throw error;
      clearChangesDraft();
      setShowModal(null);
      setChangesNote('');
      setRevising(true);
      setWorking({ text: 'Working on the revision. This can take a few minutes.', done: false });
      setBanner({
        tone: 'ok',
        text: isAgencyReviewNow
          ? 'Got it. The revision comes back to you here before the client sees anything.'
          : "Thanks. We have your notes and we're working on the revision. We'll send you the updated draft for review soon."
      });
      // Reload so the page shows the flagged state; stay on the review
      // screen so the banner remains visible and the client sees exactly
      // where the draft is.
      await loadDraft();
    } catch (e) {
      setBanner({ tone: 'err', text: `Couldn't send: ${e.message || e}` });
      setShowModal(null);
    } finally { setBusy(false); }
  }

  if (state.loading) return <p className="lede">Loading the draft…</p>;
  if (state.error)   return (
    <div className="empty">
      <h2>Couldn't open the draft</h2>
      <p>{state.error}</p>
      <p><Link to="/">← Back to your inbox</Link></p>
    </div>
  );

  const d = state.draft;

  // Hotfix: the current revision is still behind the agency review gate. The
  // client gets the banner only: no preview, no images, no actions. (The view
  // already nulls the preview path and storage refuses the file.)
  if (!asAgency && d.gate_pending) {
    return (
      <>
        <div className="reader-topbar">
          <div>
            <h1>{d.title || slug}</h1>
            <p className="lede" style={{ margin: 0 }}>
              <Link to="/">← All items</Link>
              {d.publish_date && <> · scheduled for {formatDate(d.publish_date)}</>}
            </p>
          </div>
        </div>
        {working && working.done === false && <Working text={working.text} done={working.done} tone={working.tone || 'info'} />}
        <div className="banner banner-info">We're updating your draft. We'll let you know when it's ready.</div>
      </>
    );
  }

  const alreadyApproved = d.status === 'approved';
  const isFlagged = d.status === 'flagged';
  const isAgencyReview = d.status === 'agency_review';   // Session 6: behind the agency gate
  // Session 8 (B2): once the draft is with the client, the agency's screen is
  // read-only. No approve / request-changes, no image edits; preview stays.
  // UI only: _portal_guard_job rejects the wrong caller server-side anyway.
  // Hotfix: a gate-pending draft (e.g. flagged by the agency) never reached the client.
  // Session 13: agency_only drafts never wait on a client; the agency's approve is final.
  const agencyOnly = d.approval_mode === 'agency_only';
  const sentToClient = asAgency && !dlOwner && !agencyOnly && !d.gate_pending && ['sent', 'awaiting_reply', 'flagged'].includes(d.status);
  const releases = d.review_gate?.releases || [];
  const sentAt = releases.length ? releases[releases.length - 1]?.at : null;
  const canAct = !alreadyApproved && !isFlagged && !isAgencyReview && !sentToClient;
  const canEditImages = (canAct || (isAgencyReview && asAgency)) && !alreadyApproved;
  // Session 12B: approved (email reply or automatically) without the featured
  // image a Partner API site needs. Only the hero can be added; it publishes on
  // schedule once it's in, no re-approval (portal_attach_image checks this too).
  const needsImage = alreadyApproved && !!d.needs_image;
  const released = (d.review_gate?.releases || []).length > 0;
  // Hotfix: mirrors portal_release_draft, which refuses these server-side.
  const needsLeena = (d.self_check?.needs_input || []).length > 0 || d.self_check?.proof_status === 'warn';
  // Session 66: the styled preview already shows their edit when it was
  // uploaded after the edit was saved.
  const previewFresh = !d.body_md_edited
    || (!!d.preview_uploaded_at && !!d.body_edited_at && new Date(d.preview_uploaded_at) >= new Date(d.body_edited_at));
  const canReopen = alreadyApproved && d.publish_status === 'pending'
    && (!d.publish_due_at || new Date(d.publish_due_at) > new Date());

  return (
    <>
      <div className="reader-topbar">
        <div>
          <h1>{d.title || slug}</h1>
          <p className="lede" style={{ margin: 0 }}>
            <Link to="/">← All items</Link>
            {d.publish_date && <> · scheduled for {formatDate(d.publish_date)}</>}
            {alreadyApproved && <> · <strong>already approved</strong></>}
            {isFlagged && <> · <strong>revision in progress</strong></>}
            {isAgencyReview && <> · <strong>with Digital Lotus for review</strong></>}
            {d.revision > 0 && <> · revision {d.revision}</>}
          </p>
        </div>
      </div>

      {isAgencyReview && agencyOnly && (
        <div className="banner banner-gate">
          <strong>Needs your final approval.</strong> This client has agency approval only, so nobody
          else signs off. Check the quality panel, add the hero image, then use <strong>Approve (final)</strong>.
        </div>
      )}
      {isAgencyReview && !agencyOnly && (
        <div className="banner banner-gate">
          <strong>Needs your review before the client sees it.</strong> The client has not been
          notified. Check the quality panel, add the hero image, then use <strong>Send to client</strong>.
          {released && ' This revision follows a release you already made; the client is waiting on it.'}
        </div>
      )}

      {/* Session 29: what the post is built to do, before anyone changes it */}
      <OptimisedFor draft={d} />


      {/* Session 13: client_deadline clock, and the result of an automatic approval */}
      {d.auto_approves && !alreadyApproved && <DeadlineNote at={d.deadline_at} agency={asAgency} />}
      {alreadyApproved && d.auto_approved_at && <AutoApprovedNote at={d.auto_approved_at} />}

      {needsImage && (
        <div className="banner banner-gate">
          {asAgency
            ? <><strong>Approved, waiting on the featured image.</strong> The client has been asked to add it. You can add the hero image below too; it publishes {d.publish_date ? `on ${formatDate(d.publish_date)}` : 'on schedule'} once it's in, no re-approval.</>
            : <><strong>One thing left: the featured image.</strong> Add the hero image below, the photo that sits at the top of the post. It'll publish {d.publish_date ? `on ${formatDate(d.publish_date)}` : 'on schedule'} once the image is in.</>}
        </div>
      )}

      {sentToClient && (
        <div className="banner banner-waiting">
          Sent to client{sentAt || d.updated_at ? ` on ${formatDate(sentAt || d.updated_at)}` : ''}, waiting for client approval.
        </div>
      )}

      {isFlagged && (
        <div className="banner banner-info">
          {asAgency
            ? 'Notes received. The revision is being written and comes back for review when it is ready.'
            : "Thanks for your notes. We're working on the revision now and we'll send you the updated draft as soon as it's ready."}
        </div>
      )}

      {banner && <div className={`banner ${banner.tone === 'ok' ? 'banner-ok' : 'banner-err'}`}>{banner.text}</div>}

      <WhyThisPost
        clientSummary={d.client_summary}
        primaryKeyword={d.primary_keyword}
        moneyPage={d.money_page}
        publishDate={d.publish_date}
        whyNow={d.why_now}
        selfCheck={d.self_check}
        tags={d.tags}
        defaultOpen={isAgencyReview}
      />

      {working && <Working text={working.text} done={working.done} tone={working.tone || 'info'} />}
      {previewPending && !working && (
        <Working text="Updating your preview. This can take a few minutes." />
      )}

      {isAgency && (
        <div className="as-client-bar">
          <label className="as-client-switch">
            <input type="checkbox" checked={viewAsClient}
                   onChange={(e) => setViewAsClient(e.target.checked)} />
            <span>See it as the client does</span>
          </label>
          <span className="hint" style={{ margin: 0 }}>
            {viewAsClient
              ? 'Your agency panels are hidden and the page reads as the client\'s. Anything you click still runs as you.'
              : 'On a draft that is with the client, your view is read-only — this shows you their side of it.'}
          </span>
        </div>
      )}

      {/* Session 32: Leena's callout. The three routes, before the article,
          so nobody arrives thinking they have to rewrite it themselves. */}
      {canAct && <ReviewCallout canEdit={canAct} />}

      {/* Session 31: one preview, at the top, owning the editor door */}
      <PostAndEditor
        key={d.job_id}
        draft={d}
        canEdit={canAct}
        previewHtml={state.previewHtml}
        previewRef={previewRef}
        slug={slug}
        onSaved={onTextSaved}
        onRefresh={onRefreshPreview}
        refreshing={refreshing}
        previewFresh={previewFresh}
      />

      {/* Session 32: named by the headline each photo sits under, with Move
          to on the same row, and shared with the ad hoc review page. */}
      <ImageSlotsSection
        draft={d}
        canEdit={canEditImages}
        fills={fills}
        uploading={uploading}
        skipping={skipping}
        onUploadSlot={onUploadSlot}
        onToggleSkip={onToggleSkip}
        onChanged={loadDraft}
        needsHero={needsImage}
      />

      {canAct && (
        <div className="review-actions">
          <button className="btn" onClick={() => setShowModal('changes')} disabled={busy}>
            Request changes
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowModal('approve')}
            disabled={busy}
          >
            {asAgency && agencyOnly ? 'Approve (final)' : 'Approve'}
          </button>
        </div>
      )}

      {canReopen && (
        <div className="review-actions">
          <span className="hint" style={{ marginRight: 'auto', marginTop: 8 }}>
            Approved. {d.publish_due_at ? `Scheduled for ${new Date(d.publish_due_at).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.` : ''}
            {' '}<Link to={clientPath('/blogs', activeId)}>Download it under Blogs</Link>.
          </span>
          {/* Session 43: this is the block people read once a post is approved
              and scheduled, so the instructions belong here too — not only
              beside the download on the Blogs list. */}
          <PostingInstructions platform={activeClient?.platform} />
          <button className="btn" onClick={() => setShowModal('reopen')} disabled={busy}>Move back to draft</button>
        </div>
      )}

      {showModal === 'reopen' && (
        <Modal onClose={() => setShowModal(null)}>
          <h2>Move this post back to draft?</h2>
          <p>The scheduled publish is cancelled and the post goes back to review{asAgency ? '' : '. Digital Lotus will hear about it'}. Nothing publishes until it is approved again.</p>
          <div className="review-actions">
            <button className="btn" onClick={() => setShowModal(null)} disabled={busy}>Cancel</button>
            <button className="btn btn-primary" onClick={onReopen} disabled={busy}>{busy ? 'Moving…' : 'Yes, move back to draft'}</button>
          </div>
        </Modal>
      )}

      {isAgencyReview && asAgency && (
        <div className="review-actions">
          <button className="btn" onClick={() => setShowModal('changes')} disabled={busy}>
            Request changes
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal('release')} disabled={busy}>
            {agencyOnly ? 'Approve (final)' : 'Send to client'}
          </button>
        </div>
      )}

      {showModal === 'release' && (
        <Modal onClose={() => setShowModal(null)}>
          <h2>{agencyOnly ? 'Approve this draft as final?' : 'Send this draft to the client?'}</h2>
          {approveBlockers.length > 0 && (
            <p className="hint">Still missing: {approveBlockers.join(', ')}. You can send anyway; the client can add images before approving.</p>
          )}
          {needsLeena && (
            <p className="hint">Remove the Leena's-input note first. {d.self_check?.needs_input?.length || 1} line{(d.self_check?.needs_input?.length || 1) === 1 ? '' : 's'} still marked [NEEDS LEENA]; use Request changes to have it taken out.</p>
          )}
          {agencyOnly ? (
            <p>This is the final approval: nobody else signs off. The post is scheduled for its publish date ({formatDate(d.publish_date)}).</p>
          ) : d.approval_mode === 'client_deadline' ? (
            <p>They get the "draft ready" email and the draft appears in their portal inbox. Their clock starts now: it approves automatically if they don't respond by the deadline, with one reminder first.</p>
          ) : (
            <p>They get the "draft ready" email and the draft appears in their portal inbox. The nudge clock starts now.</p>
          )}
          <div className="review-actions">
            <button className="btn" onClick={() => setShowModal(null)} disabled={busy}>Cancel</button>
            <button className="btn btn-primary" onClick={onRelease} disabled={busy || needsLeena}>
              {busy ? (agencyOnly ? 'Approving…' : 'Sending…') : (agencyOnly ? 'Yes, approve (final)' : 'Yes, send to client')}
            </button>
          </div>
        </Modal>
      )}

      {showModal === 'approve' && (
        <Modal onClose={() => setShowModal(null)}>
          <h2>Approve this draft?</h2>
          {approveBlockers.length > 0 ? (
            <>
              <p>A few things first:</p>
              <ul>
                {approveBlockers.map((m) => <li key={m}>{m}</li>)}
              </ul>
              <div className="review-actions">
                <button className="btn" onClick={() => setShowModal(null)}>Close</button>
              </div>
            </>
          ) : (
            <>
              <p style={{ marginBottom: 8 }}>Publish date</p>
              <input
                type="date"
                value={publishDate || ''}
                min={tomorrowIsoPT()}
                onChange={(e) => setPublishDate(e.target.value)}
                disabled={busy}
              />
              <p className="hint" style={{ marginTop: 6 }}>
                Publishes at 8am Pacific on this date.
              </p>
              <div className="review-actions">
                <button className="btn" onClick={() => setShowModal(null)} disabled={busy}>Cancel</button>
                <button className="btn btn-primary" onClick={onApprove} disabled={busy || !publishDate}>
                  {busy ? 'Approving…' : 'Yes, approve'}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {showModal === 'changes' && (
        <Modal onClose={() => setShowModal(null)}>
          <h2>Request changes</h2>
          <p>{isAgencyReview ? 'Tell the drafter what to change. The revision comes back to you before the client sees it.' : "Tell us what to change. We'll rewrite and send you a new draft."}</p>
          <textarea
            ref={changesRef}
            className="review-textarea"
            rows={6}
            placeholder="e.g. Tighten the intro, drop the third example, add a paragraph on GBP posts."
            value={changesNote}
            onChange={(e) => setChangesNote(e.target.value)}
            disabled={busy}
          />
          <MicButton value={changesNote} onChange={setChangesNote} inputRef={changesRef} disabled={busy} clientId={activeId} hint />
          <div className="review-actions">
            <button className="btn" onClick={() => setShowModal(null)} disabled={busy}>Cancel</button>
            <button className="btn btn-primary" onClick={onRequestChanges} disabled={busy || !changesNote.trim()}>
              {busy ? 'Sending…' : 'Send changes'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

// Session 29: what this blog was built to do. Clients see the article and
// assume the length, the headings and the questions at the end are style
// choices. They are not, and when a client edits without knowing that, they
// quietly undo the thing they are paying for. Naming it is the guardrail:
// it informs, it does not prevent. Agency users see the same panel, since
// they wrote these fields and checking them is useful.
function OptimisedFor({ draft }) {
  const keyword = (draft.primary_keyword || '').trim();
  const money = (draft.money_page || '').trim();
  const type = (draft.post_type || '').trim();
  if (!keyword && !money && !type) return null;

  const TYPE_LABEL = {
    how_to: 'a how-to', listicle: 'a list', guide: 'a guide',
    comparison: 'a comparison', local: 'a local page', faq: 'an FAQ',
  };

  return (
    <section className="optimised-for">
      <h2>What this post is built to do</h2>
      <dl>
        {keyword && (
          <div>
            <dt>Search it targets</dt>
            <dd>{keyword}</dd>
          </div>
        )}
        {type && (
          <div>
            <dt>Shape</dt>
            <dd>Written as {TYPE_LABEL[type] || type.replace(/_/g, ' ')}, because that is what tends to rank for this kind of search.</dd>
          </div>
        )}
        {money && (
          <div>
            <dt>Page it points to</dt>
            <dd>{money}</dd>
          </div>
        )}
      </dl>
      <p className="hint">
        Edit this freely, it is your post. Just keep the search above in the title
        and the opening, keep the headings roughly as they are, and leave the
        questions at the end in. Those are the parts doing the ranking work.
      </p>
    </section>
  );
}

function Modal({ onClose, children }) {
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
